/**
 * Theme colors matching Flutter AppColors
 * 
 * These colors should be used consistently across the application.
 * When a new color is needed, add it here first before using inline styles.
 */

// Dark theme colors (current default)
export const DARK_THEME = {
  transparent: 'transparent',
  primary: '#1a1a1a', // Very dark grey
  secondary: '#2a2a2a', // Dark grey
  accent: '#ff6b6b', // Bright accent color  
  background: '#000000', // Pure black background
  backgroundSecondary: '#080808', // Very dark grey for secondary backgrounds
  reversedBackground: '#f5f5f5', // primary color of light theme, used for reversed elements in dark mode
  topBarBackdrop: 'rgba(0, 0, 0, 0.4)', // Semi-transparent black overlay for top bar
  surface: '#1e1e1e', // Dark surface for cards
  modalBackground: '#1e1e1e', // Dark surface for modal backgrounds
  modalOverlay: 'rgba(0, 0, 0, 0.5)', // Semi-transparent black overlay
  loadingOverlay: 'rgba(0, 0, 0, 0.3)', // Lighter overlay for saving/loading states
  gradientBackground: 'rgba(0, 0, 0, 0.8)',
  // Live-streaming top scrim gradient (Flutter `topOverlayDecorationLiveStreaming`,
  // app_styles.dart:379-389: `backgroundColor@.4 → backgroundColor@0`, top→bottom).
  // Kept a black scrim in BOTH web themes because it darkens the top of the
  // always-dark live-video surface so the white top-bar controls stay readable —
  // the native LIVE app value (`backgroundColor = black` on native). A CSS
  // gradient string, consumed as `background`.
  liveStreamingTopOverlay: 'linear-gradient(to bottom, rgba(0, 0, 0, 0.4), rgba(0, 0, 0, 0))',
  // Live-streaming bottom scrim gradient (Flutter `bottomOverlayDecorationLiveStreaming`,
  // app_styles.dart:391-401: `backgroundColor@.4 → backgroundColor@0`, bottom→top).
  // Same black scrim in BOTH web themes so the live-chat rows read over the
  // always-dark video surface. A CSS gradient string, consumed as `background`.
  liveStreamingBottomOverlay: 'linear-gradient(to top, rgba(0, 0, 0, 0.4), rgba(0, 0, 0, 0))',
  black: '#000000', // Dark surface for light controls
  border: '#3a3a3a', // Subtle border color for dividers and separators
  disabled: '#808080', // Dark grey for disabled elements
  disabledTab: '#C0C0C0', // Light grey for disabled tabs
  white: '#ffffff', // Pure white
  exclusive: '#C9952C', // Amber/yellow - used for borders, highlights, coins, etc.
  exclusiveTransparent: 'rgba(201, 149, 44, 0.5)', // theme.exclusive (#C9952C) at 0.5 alpha — video-call send-gift circle (Flutter AppColors.exclusive @ .5, video_call_screen.dart:1206)
  // Last-gift banner pill background (shared LastGiftOverlay, both video-call &
  // live-streaming twins). Flutter `AppColors.backgroundColorLight.withValues(alpha: .2)`
  // = native `#171717 @ .2` (video_call_screen.dart:578-591 / live_streaming_screen.dart:946-957).
  // Kept a translucent DARK in both web themes (the banner sits on the always-dark
  // video surface, like the live-streaming scrims) so the gold coin icon keeps contrast.
  lastGiftPillBackground: 'rgba(23, 23, 23, 0.2)',
  whiteLight: '#f5f5f5', // Off-white for light elements
  whiteLightTransparent: 'rgba(245, 245, 245, 0.5)', // theme.whiteLight (#f5f5f5) at 0.5 alpha — video-call bottom-bar overlay (Flutter bottomOverlayDecorationVideoCall, app_styles.dart:376)
  danger: '#dc2626', // Red for errors and danger actions
  dangerHover: '#b91c1c', // Darker red for hover states
  warning: '#FFA500', // Orange for warnings
  activeRed: '#D10033', // Bright red for active states (Like button, recording, etc.)
  // Unread-notification row background. Set to the dark-theme `secondary`
  // value (#2a2a2a) per user review 3 — a subtle dark highlight rather than the
  // former translucent white (Flutter web `backgroundColorLight = 0xB3FFFFFF`),
  // which read as a near-white block on the dark page.
  notificationUnread: '#2a2a2a', // matches DARK_THEME.secondary
  blue: '#1E90FF', // Dodger blue for links and highlights
  lightBlue: '#ADD8E6', // Light blue for secondary highlights
  toggleActive: '#34C759', // iOS CupertinoSwitch green for active toggles
  toggleInactive: '#808080', // Grey for inactive toggles
  chat: {
    ownMessage: '#D10033', // Chat bubble background for own messages (Flutter `AppColors.ownMessageColor = activeRed`)
    peerMessage: '#1e1e1e', // Chat bubble background for peer messages. Web-side aligned to `surface` (deliberate deviation from Flutter's `AppColors.peerMessageColor` on kIsWeb = `0xB3FFFFFF`).
    replyMessage: '#404040' // Community reply-preview bubble background (Flutter `AppColors.replyMessageColor = 0xff404040`).
  },
  shadow: 'rgba(0, 0, 0, 0.3)', // Shadow color for elevated elements
  text: {
    primary: '#ffffff', // Pure white for primary text
    secondary: '#DCDCDC', // Light grey for secondary text
    selected: '#C9952C' // Amber for selected text
  },
  icon: {
    primary: '#ffffffff', // Pure white for primary icons
    secondary: '#acababff', // Light grey for secondary icons
    selected: '#ffffffff', // Pure white for selected icons
    unselected: '#acababff' // Light grey for unselected icons
  },
  button: {
    primary: '#C9952C', // Amber for primary buttons
    secondary: '#2a2a2a', // Dark grey for secondary buttons
    ghost: 'transparent', // Transparent for ghost buttons
    danger: '#dc2626', // Red for danger buttons
    round: '#00000066' // Semi-transparent black for round buttons (e.g. top bar icons)
  },
  stepper: {
    progressInactive: '#6b7280', // Light grey for inactive progress segments
    progressActive: '#ffffffff', // Pure white for active progress segment
    progressCompleted: '#C9952C' // Exclusive for completed progress segments
  },
  separator: '#3a3a3a' // Subtle separator color for dividers
} as const;

// Light theme colors
export const LIGHT_THEME = {
  transparent: 'transparent',
  primary: '#f5f5f5', // Very light grey
  secondary: '#e8e6e3', // Warm light grey
  accent: '#ff6b6b', // Bright accent color
  background: '#faf9f7', // Off-white background
  backgroundSecondary: '#f0f0f0', // Very light grey for secondary backgrounds
  reversedBackground: '#1a1a1a', // primary color of dark theme, used for reversed elements in light mode
  topBarBackdrop: 'rgba(255, 255, 255, 0.4)', // Semi-transparent white overlay for top bar
  surface: '#ffffff', // Pure white surface
  modalBackground: '#e8e6e3', // Warm light grey for modal backgrounds  
  modalOverlay: 'rgba(0, 0, 0, 0.5)', // Semi-transparent black overlay
  loadingOverlay: 'rgba(0, 0, 0, 0.3)', // Lighter overlay for saving/loading states
  gradientBackground: 'rgba(0, 0, 0, 0.8)',
  // Live-streaming top scrim gradient (Flutter `topOverlayDecorationLiveStreaming`,
  // app_styles.dart:379-389). Kept a black scrim in BOTH web themes — it darkens
  // the top of the always-dark live-video surface for readable white controls.
  liveStreamingTopOverlay: 'linear-gradient(to bottom, rgba(0, 0, 0, 0.4), rgba(0, 0, 0, 0))',
  // Live-streaming bottom scrim gradient (Flutter `bottomOverlayDecorationLiveStreaming`,
  // app_styles.dart:391-401). Same black scrim in BOTH web themes — the live-chat
  // rows read over the always-dark video surface.
  liveStreamingBottomOverlay: 'linear-gradient(to top, rgba(0, 0, 0, 0.4), rgba(0, 0, 0, 0))',
  black: '#000000', // Dark surface for light controls
  border: '#d1cfc8', // Subtle border color for dividers and separators
  disabled: '#9ca3af', // Medium grey for disabled elements
  disabledTab: '#6b7280', // Darker grey for disabled tabs
  white: '#ffffff', // Pure white
  exclusive: '#C9952C', // Amber/yellow - used for borders, highlights, coins, etc.
  exclusiveTransparent: 'rgba(201, 149, 44, 0.5)', // theme.exclusive (#C9952C) at 0.5 alpha — video-call send-gift circle (Flutter AppColors.exclusive @ .5, video_call_screen.dart:1206)
  // Last-gift banner pill background (shared LastGiftOverlay, both video-call &
  // live-streaming twins). Flutter `AppColors.backgroundColorLight.withValues(alpha: .2)`
  // = native `#171717 @ .2` (video_call_screen.dart:578-591 / live_streaming_screen.dart:946-957).
  // Kept a translucent DARK in both web themes (the banner sits on the always-dark
  // video surface, like the live-streaming scrims) so the gold coin icon keeps contrast.
  lastGiftPillBackground: 'rgba(23, 23, 23, 0.2)',
  whiteLight: '#f5f5f5', // Off-white for light elements
  whiteLightTransparent: 'rgba(245, 245, 245, 0.5)', // theme.whiteLight (#f5f5f5) at 0.5 alpha — video-call bottom-bar overlay (Flutter bottomOverlayDecorationVideoCall, app_styles.dart:376)
  danger: '#dc2626', // Red for errors and danger actions
  dangerHover: '#b91c1c', // Darker red for hover states
  warning: '#FFA500', // Orange for warnings
  activeRed: '#D10033', // Bright red for active states (Like button, recording, etc.)
  // Unread-notification row background. Set to the light-theme `secondary`
  // value (#e8e6e3) per user review 3 (was the translucent white
  // `0xB3FFFFFF` Flutter web mapping).
  notificationUnread: '#e8e6e3', // matches LIGHT_THEME.secondary
  blue: '#1E90FF', // Dodger blue for links and highlights
  lightBlue: '#ADD8E6', // Light blue for secondary highlights
  toggleActive: '#34C759', // iOS CupertinoSwitch green for active toggles
  toggleInactive: '#9ca3af', // Grey for inactive toggles
  chat: {
    ownMessage: '#D10033', // Chat bubble background for own messages (Flutter `AppColors.ownMessageColor = activeRed`)
    peerMessage: '#ffffff', // Chat bubble background for peer messages. Web-side aligned to `surface` (deliberate deviation from Flutter's `AppColors.peerMessageColor` on kIsWeb = `0xB3FFFFFF`).
    replyMessage: '#404040' // Community reply-preview bubble background (Flutter `AppColors.replyMessageColor = 0xff404040`).
  },
  shadow: 'rgba(0, 0, 0, 0.15)', // Shadow color for elevated elements (lighter in light theme)
  text: {
    primary: '#1a1a1a', // Almost black for primary text
    secondary: '#4b5563', // Dark grey for secondary text
    selected: '#C9952C' // Amber for selected text
  },
  icon: {
    primary: '#6b7280', // Dark grey for primary icons
    secondary: '#6b7280', // Dark grey for secondary icons
    selected: '#ff0000', // Bright red for selected icons
    unselected: '#6b7280' // Dark grey for unselected icons
  },
  button: {
    primary: '#C9952C',
    secondary: '#6b7280', // Light grey
    ghost: 'transparent', // Transparent for ghost buttons
    danger: '#dc2626', // Red for danger buttons
    round: '#00000066' // black 66
  },
  stepper: {
    progressInactive: '#6b7280', // Light grey for inactive progress segments
    progressActive: '#ffffffff', // Pure white for active progress segment
    progressCompleted: '#C9952C' // Exclusive for completed progress segments
  },
  separator: '#d1cfc8' // Subtle separator color for dividers
} as const;

export type ThemeColors = typeof DARK_THEME;
export type ThemeMode = 'light' | 'dark' | 'system';
